import type { ColorToken } from "../lane-view";

export type HandColor = {
  /** Hex value (0xRRGGBB), for Pixi.js Graphics. */
  hex: number;
  /** CSS string (`#rrggbb` or `rgba(...)`), usable directly in inline styles. */
  css: string;
  /** Original CSS var value before normalization (e.g. raw `hsl(...)` string). */
  cssRaw: string;
  /** 0..1 alpha if present in source color. Defaults to 1. */
  alpha: number;
};

export type HandPalette = Record<ColorToken, HandColor>;

const TOKEN_TO_CSS_VAR: Record<ColorToken, string> = {
  'hand.right': '--color-hand-right',
  'hand.left': '--color-hand-left',
  'live': '--color-hand-live',
  'default': '--color-hand-default',
};

/** Fallback used when a CSS var is missing or in non-DOM contexts. */
const SAFE_FALLBACK: HandColor = {
  hex: 0xcccccc,
  css: '#cccccc',
  cssRaw: '#cccccc',
  alpha: 1,
};

/**
 * Reads the hand color palette from CSS variables.
 *
 * Why client-only: touches `document` (canvas trick) and `getComputedStyle`.
 * Calling this from a Server Component will throw at evaluation time.
 */
export function readHandPalette(root?: HTMLElement): HandPalette {
  const target = root ?? document.documentElement;
  const styles = getComputedStyle(target);
  const palette = {} as HandPalette;

  for (const token of Object.keys(TOKEN_TO_CSS_VAR) as ColorToken[]) {
    const cssVar = TOKEN_TO_CSS_VAR[token];
    const raw = styles.getPropertyValue(cssVar).trim();

    if (!raw) {
      palette[token] = SAFE_FALLBACK;
      continue;
    }

    palette[token] = normalizeColor(raw);
  }

  return palette;
}

function normalizeColor(raw: string): HandColor {
  // Use the canvas 2D context as a normaliser: assigning any valid CSS color
  // string to `fillStyle` returns a canonical `#rrggbb` (no alpha) or
  // `rgba(r, g, b, a)` representation, regardless of input format.
  const ctx = document.createElement('canvas').getContext('2d');

  if (!ctx) {
    return { ...SAFE_FALLBACK, cssRaw: raw };
  }

  ctx.fillStyle = '#000000';
  ctx.fillStyle = raw;
  const normalized = ctx.fillStyle as string;

  return parseNormalized(normalized, raw);
}

function parseNormalized(normalized: string, raw: string): HandColor {
  if (normalized.startsWith('#')) {
    const hex = parseInt(normalized.slice(1), 16);

    return {
      hex,
      css: normalized,
      cssRaw: raw,
      alpha: 1,
    };
  }

  // rgba(r, g, b, a), canvas always normalises with this exact shape.
  const match = normalized.match(/rgba?\(([^)]+)\)/);

  if (!match) {
    return { ...SAFE_FALLBACK, cssRaw: raw };
  }

  const parts = match[1].split(',').map((s) => parseFloat(s.trim()));
  const r = Math.max(0, Math.min(255, parts[0] | 0));
  const g = Math.max(0, Math.min(255, parts[1] | 0));
  const b = Math.max(0, Math.min(255, parts[2] | 0));
  const alpha = parts.length >= 4 && Number.isFinite(parts[3]) ? parts[3] : 1;
  const hex = (r << 16) | (g << 8) | b;
  const css = `rgba(${r}, ${g}, ${b}, ${alpha})`;

  return {
    hex,
    css,
    cssRaw: raw,
    alpha,
  };
}
